import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import {
  X,
  Video,
  Calendar,
  Clock,
  Shield,
  Mail,
  Phone,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import {
  hasZoomScheduling,
  meetingOptions,
  timezoneNote,
  CONTACT_PHONE,
  CONTACT_PHONE_TEL,
} from "../config/schedule";

const ScheduleCallModal = ({ isOpen, onClose }) => {
  const reduceMotion = useReducedMotion();
  const location = useLocation();
  const navigate = useNavigate();

  const [showFallback, setShowFallback] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    preferredTime: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setShowFallback(false);
      setSubmitStatus(null);
      setFormData({ name: "", email: "", preferredTime: "", message: "" });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const scrollToContact = () => {
    onClose();
    if (location.pathname === "/") {
      setTimeout(
        () =>
          document.querySelector("#contact")?.scrollIntoView({
            behavior: "smooth",
          }),
        300
      );
    } else {
      navigate("/#contact");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFallbackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const response = await fetch(`${API_URL}/api/schedule-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get("content-type");
      let result;

      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        throw new Error("Backend returned non-JSON response");
      }

      if (response.ok && result.success) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", preferredTime: "", message: "" });
      } else {
        throw new Error(result.error || "Failed to send request");
      }
    } catch (error) {
      console.error("Schedule request error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openZoom = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const overlayVariants = reduceMotion
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
    : { hidden: { opacity: 0 }, visible: { opacity: 1 } };

  const modalVariants = reduceMotion
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.95, y: 20 },
        visible: { opacity: 1, scale: 1, y: 0 },
      };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          transition={{ duration: reduceMotion ? 0 : 0.2 }}
        >
          <motion.button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            aria-label="Close schedule call dialog"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-call-title"
            className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-y-auto glass-panel border-white/10 rounded-t-2xl sm:rounded-2xl shadow-2xl shadow-black/50"
            variants={modalVariants}
            transition={{ duration: reduceMotion ? 0 : 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 p-6 pb-4 bg-dark-400/95 backdrop-blur-xl border-b border-white/5 rounded-t-2xl sm:rounded-t-2xl">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary-500/30 bg-primary-500/10 text-primary-300 text-xs font-medium mb-3">
                  <Video className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden />
                  Zoom Meeting
                </div>
                <h2
                  id="schedule-call-title"
                  className="font-display text-xl sm:text-2xl font-bold text-white"
                >
                  Schedule a Call
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  Free consultation · No commitment
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>

            <div className="p-6 pt-4 space-y-5">
              {hasZoomScheduling && !showFallback ? (
                <>
                  <p className="text-sm text-gray-400">
                    Pick a session length and book instantly on Zoom.{" "}
                    <span className="text-gray-500">{timezoneNote}</span>
                  </p>

                  <div className="space-y-3">
                    {meetingOptions.map((option) => (
                      <motion.button
                        key={option.id}
                        type="button"
                        onClick={() => openZoom(option.url)}
                        className="w-full text-left glass-panel-hover p-4 border border-white/10 hover:border-primary-500/40 transition-colors group"
                        whileHover={reduceMotion ? {} : { scale: 1.01 }}
                        whileTap={reduceMotion ? {} : { scale: 0.99 }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center text-primary-400 flex-shrink-0">
                              <Calendar
                                className="w-5 h-5"
                                strokeWidth={1.75}
                                aria-hidden
                              />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-white group-hover:text-primary-300 transition-colors">
                                  {option.title}
                                </span>
                                <span className="text-xs text-primary-400 font-medium">
                                  {option.minutes} min
                                </span>
                                {option.badge && (
                                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-accent-emerald/10 text-accent-emerald border border-accent-emerald/20">
                                    {option.badge}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-0.5">
                                {option.description}
                              </p>
                            </div>
                          </div>
                          <ExternalLink
                            className="w-4 h-4 text-gray-600 group-hover:text-primary-400 flex-shrink-0 mt-1 transition-colors"
                            strokeWidth={1.75}
                            aria-hidden
                          />
                        </div>
                      </motion.button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Shield className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden />
                    Secure Zoom link · Your info stays private
                  </div>
                </>
              ) : (
                <>
                  {!hasZoomScheduling && (
                    <div className="p-4 rounded-xl bg-primary-500/10 border border-primary-500/20 text-sm text-gray-300">
                      <p className="font-medium text-white mb-1">
                        We'll send you a Zoom link within 24 hours
                      </p>
                      <p className="text-gray-400">
                        Share your preferred time below and our team will confirm
                        your call.
                      </p>
                    </div>
                  )}

                  {submitStatus === "success" ? (
                    <motion.div
                      className="text-center py-8"
                      initial={reduceMotion ? {} : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <CheckCircle2
                        className="w-12 h-12 text-accent-emerald mx-auto mb-4"
                        strokeWidth={1.75}
                      />
                      <h3 className="font-display text-lg font-bold text-white mb-2">
                        Request received!
                      </h3>
                      <p className="text-sm text-gray-400 mb-6">
                        We'll email you a Zoom link within 24 hours to confirm
                        your call.
                      </p>
                      <button
                        type="button"
                        onClick={onClose}
                        className="btn-primary text-sm px-6 py-3"
                      >
                        Done
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleFallbackSubmit} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1.5">
                            Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2.5 bg-dark-200/80 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-primary-500 transition-colors text-sm"
                            placeholder="Your name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1.5">
                            Email *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2.5 bg-dark-200/80 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-primary-500 transition-colors text-sm"
                            placeholder="you@company.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1.5">
                          Preferred date & time *
                        </label>
                        <div className="relative">
                          <Clock
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                            strokeWidth={1.75}
                            aria-hidden
                          />
                          <input
                            type="datetime-local"
                            name="preferredTime"
                            value={formData.preferredTime}
                            onChange={handleChange}
                            required
                            className="w-full pl-10 pr-4 py-2.5 bg-dark-200/80 border border-white/10 rounded-xl text-white focus:outline-none focus:border-primary-500 transition-colors text-sm [color-scheme:dark]"
                          />
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{timezoneNote}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1.5">
                          What would you like to discuss?
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={3}
                          className="w-full px-4 py-2.5 bg-dark-200/80 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-primary-500 transition-colors resize-none text-sm"
                          placeholder="Brief project overview (optional)"
                        />
                      </div>

                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full btn-primary text-sm py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        whileHover={!isSubmitting && !reduceMotion ? { scale: 1.01 } : {}}
                        whileTap={!isSubmitting && !reduceMotion ? { scale: 0.99 } : {}}
                      >
                        {isSubmitting ? "Sending..." : "Request Zoom Call →"}
                      </motion.button>

                      {submitStatus === "error" && (
                        <p className="text-sm text-red-400 text-center">
                          Couldn't send request. Email us at{" "}
                          <a
                            href="mailto:master@purposelabs.in"
                            className="underline hover:text-red-300"
                          >
                            master@purposelabs.in
                          </a>
                        </p>
                      )}
                    </form>
                  )}
                </>
              )}

              {submitStatus !== "success" && (
                <div className="pt-4 border-t border-white/5 space-y-3">
                  {hasZoomScheduling && !showFallback && (
                    <button
                      type="button"
                      onClick={() => setShowFallback(true)}
                      className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      <Mail className="w-4 h-4" strokeWidth={1.75} aria-hidden />
                      Prefer email? Request a time instead
                    </button>
                  )}

                  {hasZoomScheduling && showFallback && (
                    <button
                      type="button"
                      onClick={() => setShowFallback(false)}
                      className="w-full text-sm text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      ← Back to Zoom booking
                    </button>
                  )}

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm">
                    <button
                      type="button"
                      onClick={scrollToContact}
                      className="flex items-center gap-2 text-gray-400 hover:text-primary-400 transition-colors"
                    >
                      <Mail className="w-4 h-4" strokeWidth={1.75} aria-hidden />
                      Full contact form
                    </button>
                    <a
                      href={CONTACT_PHONE_TEL}
                      className="flex items-center gap-2 text-gray-400 hover:text-primary-400 transition-colors"
                    >
                      <Phone className="w-4 h-4" strokeWidth={1.75} aria-hidden />
                      {CONTACT_PHONE}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScheduleCallModal;
